import { defineStore } from 'pinia'
import { onSnapshot, doc } from 'firebase/firestore'
import { FirebaseFirestore } from '@capacitor-firebase/firestore'
import { db } from '../firebase'
import type { AppAnnouncement } from '../types'

const DISMISSED_ID_KEY = 'dismissed_announcement_id'
const ANNOUNCEMENT_PATH = 'appAnnouncements/current'

// 平台判斷，跟 firestoreService.ts 用同一套邏輯：
// iOS 用 Capacitor 原生 API（Web SDK 的即時監聽在 WKWebView 內不可靠），Android/Web 用 Web SDK。
const isCapacitor = typeof window !== 'undefined' && (window as any).Capacitor !== undefined
const platform = isCapacitor ? (window as any).Capacitor?.getPlatform?.() : 'web'
const useNativeFirestore = isCapacitor && platform === 'ios'

// 監聽器控制代碼放在 store 外層（跟 useAuthStore.ts 的 profileUnsubscribe 同一種作法），
// 避免重複註冊多個監聽器。兩種平台的控制代碼形狀不同（Web SDK 給 unsubscribe 函式，
// 原生 API 給 callbackId 字串），分開存放。
let webUnsubscribe: (() => void) | null = null
let nativeCallbackId: string | null = null

export const useAnnouncementStore = defineStore('announcement', {
  state: () => ({
    announcement: null as AppAnnouncement | null,
    dismissedId: null as string | null,
    loaded: false,
  }),

  getters: {
    /** 是否應該顯示 banner：公告存在、標記為啟用、且使用者尚未關閉過這一則 */
    visible(state): boolean {
      return !!state.announcement
        && state.announcement.active
        && state.announcement.id !== state.dismissedId
    },
  },

  actions: {
    /**
     * 開始即時監聽全域公告文件。App 開著時只要後台發布新公告（或下架），畫面會立刻反映，
     * 不需要使用者重開 App 或下拉刷新。公告是公開資料（Firestore 規則允許未登入讀取），
     * 所以在 App 啟動當下就可以開始監聽，不需要等登入完成。
     * 失敗時安靜忽略（公告是錦上添花功能，不應影響 App 其餘部分）。
     */
    async startListening() {
      this.dismissedId = localStorage.getItem(DISMISSED_ID_KEY)

      if (webUnsubscribe || nativeCallbackId) return // 避免重複註冊

      if (useNativeFirestore) {
        try {
          nativeCallbackId = await FirebaseFirestore.addDocumentSnapshotListener(
            { reference: ANNOUNCEMENT_PATH },
            (event, error) => {
              if (error) {
                console.error('[useAnnouncementStore] 監聽公告失敗:', error)
                this.loaded = true
                return
              }
              const data = event?.snapshot?.data as AppAnnouncement | undefined
              this.announcement = data ?? null
              this.loaded = true
            }
          )
        } catch (error) {
          console.error('[useAnnouncementStore] 建立公告監聽器失敗:', error)
          this.loaded = true
        }
        return
      }

      const ref = doc(db, 'appAnnouncements', 'current')
      webUnsubscribe = onSnapshot(
        ref,
        (snapshot) => {
          this.announcement = snapshot.exists() ? (snapshot.data() as AppAnnouncement) : null
          this.loaded = true
        },
        (error) => {
          console.error('[useAnnouncementStore] 監聽公告失敗:', error)
          this.loaded = true
        }
      )
    },

    /** 停止監聽（App 層級卸載時呼叫，一般情況下這個監聽器會存活整個 App 生命週期） */
    async stopListening() {
      if (webUnsubscribe) {
        webUnsubscribe()
        webUnsubscribe = null
      }
      if (nativeCallbackId) {
        const callbackId = nativeCallbackId
        nativeCallbackId = null
        try {
          await FirebaseFirestore.removeSnapshotListener({ callbackId })
        } catch (error) {
          console.error('[useAnnouncementStore] 移除公告監聽器失敗:', error)
        }
      }
    },

    /** 關閉目前這則公告。記錄其 id，之後同一則不會再顯示，但發布新公告（新 id）時會再次顯示。 */
    dismiss() {
      if (!this.announcement) return
      this.dismissedId = this.announcement.id
      localStorage.setItem(DISMISSED_ID_KEY, this.announcement.id)
    },
  },
})
